import { useCallback, useEffect, useState } from 'react';
import {
  warningForDose,
  type Banner,
  type DayView,
  type DoseCard,
  type DoseWarning,
  type PrnCard,
} from '@rx/shared';
import type { AppConfig } from '../config.js';
import { apiFetch } from '../api/client.js';
import { localDay, localRegimen, refreshAll, syncAfterWrite } from '../api/day.js';
import { queueEvent, registerDrainTriggers, unqueue } from '../offline/outbox.js';
import { getMeta, requestPersistence, setMeta } from '../offline/db.js';
import { CareTeam } from './CareTeam.js';
import { ExtraFlow, type ExtraSubstanceSubmission } from './ExtraFlow.js';
import { OverdoseSheet } from './OverdoseSheet.js';
import { SymptomFlow, type SymptomSubmission } from './SymptomFlow.js';
import { TimePicker } from './TimePicker.js';
import { VitalsFlow, type VitalsSubmission } from './VitalsFlow.js';

const UNDO_WINDOW_MS = 15 * 60 * 1000;

/**
 * The home screen IS the log screen.
 *
 * Opening the app shows today's doses as large cards. One tap logs a dose at the
 * device's current time. There is no navigation to reach the thing you came to do.
 */
export interface PatientOption {
  patientId: string;
  displayName: string;
  role: 'SELF' | 'CAREGIVER';
  isTest: boolean;
}

export function Today({
  config,
  patientId,
  readOnly = false,
  patients = [],
  onSelectPatient,
  onSignOut,
}: {
  config: AppConfig;
  patientId: string;
  readOnly?: boolean;
  patients?: PatientOption[];
  onSelectPatient?: (patientId: string) => void;
  onSignOut?: () => void;
}) {
  const [day, setDay] = useState<DayView | undefined>();
  const [pending, setPending] = useState(0);
  const [loading, setLoading] = useState(true);
  const [online, setOnline] = useState(navigator.onLine);
  const [justLogged, setJustLogged] = useState<{ eventId: string; at: number } | undefined>();
  const [flow, setFlow] = useState<'today' | 'symptom' | 'extra' | 'vitals' | 'team'>('today');
  const [vitalsOn, setVitalsOn] = useState(false);
  /** A dose held back pending the person's answer to the over-dose question. */
  const [pendingDose, setPendingDose] = useState<
    { warning: DoseWarning; log: () => Promise<void> } | undefined
  >();
  const [reportState, setReportState] = useState<'idle' | 'building' | 'error'>('idle');
  const [undoing, setUndoing] = useState(false);
  /** A dose waiting on "when did you take it?". */
  const [timing, setTiming] = useState<
    | {
        medId: string;
        medVersion: number;
        slotId: string | null;
        quantity: number;
        medName: string;
        /** Set when correcting the time of a dose already recorded. */
        replacesEventId?: string;
      }
    | undefined
  >();

  const render = useCallback(async () => {
    const local = await localDay(patientId);
    setDay(local.view);
    setPending(local.pendingCount);
    return local;
  }, [patientId]);

  // First paint comes from the cache, so a cold start with no signal still shows the
  // chart. The network refresh happens behind it and re-renders if it succeeds.
  useEffect(() => {
    // AbortController rather than a captured boolean: a plain `let cancelled = false`
    // is narrowed to the literal false by the type checker, which silently disables
    // both guards.
    const controller = new AbortController();
    // Read through a function: checking `controller.signal.aborted` directly lets the
    // type checker narrow it to false after the first guard, silently disabling every
    // later one even though an await in between can flip it.
    const aborted = (): boolean => controller.signal.aborted;
    void (async () => {
      const local = await render();
      if (aborted()) return;
      setLoading(false);
      const date = local.view?.logicalDate ?? new Date().toISOString().slice(0, 10);
      const ok = await refreshAll(config, patientId, date);
      if (aborted()) return;
      setOnline(ok);
      await render();
    })();
    return () => {
      controller.abort();
    };
  }, [config, patientId, render]);

  useEffect(() => {
    void getMeta<boolean>('vitalsEnabled').then((v) => {
      setVitalsOn(v ?? false);
    });
  }, []);

  useEffect(() => {
    void requestPersistence();
    return registerDrainTriggers(config, () => {
      void render();
    });
  }, [config, render]);

  useEffect(() => {
    const update = (): void => {
      setOnline(navigator.onLine);
    };
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);

  const commit = useCallback(
    async (draft: Parameters<typeof queueEvent>[1], takenAt?: Date) => {
      // Local write first, always. The checkmark must not wait for a network call.
      const queued = await queueEvent(patientId, draft, takenAt);
      setJustLogged({ eventId: queued.eventId, at: Date.now() });
      setFlow('today');
      const local = await render();

      // Push AND pull. Draining alone deletes the local outbox entry once the server
      // has it, so the event would exist in neither place until the next full refresh
      // and the checkmark would silently revert a second after appearing.
      const date = local.view?.logicalDate ?? new Date().toISOString().slice(0, 10);
      void syncAfterWrite(config, patientId, date).then(() => render());
    },
    [config, patientId, render],
  );

  /**
   * Log a dose, asking first only when there is genuinely something to ask.
   *
   * The check runs LOCALLY against the cached regimen and today's events, so the
   * question still appears in a basement with no signal. The server re-evaluates on
   * sync and stamps the authoritative flag.
   */
  const logDose = useCallback(
    async (
      medId: string,
      medVersion: number,
      slotId: string | null,
      quantity: number,
      takenAt?: Date,
      /**
       * True when this is the same dose being re-recorded at a corrected time.
       *
       * A correction must never raise the over-dose question. It is not an additional
       * dose, and asking "is this an extra dose?" after someone fixes a typo both
       * misleads them and, if they answer yes, writes a false extra-dose flag into the
       * record a doctor reads.
       */
      isCorrection = false,
    ) => {
      const regimen = await localRegimen(patientId);
      const med = regimen?.medications.find((m) => m.medId === medId);

      // Read the day FRESH rather than from the render that created this callback.
      // The captured value is stale after an undo — and after any dose logged moments
      // earlier — so the check would be made against a chart row that no longer exists.
      const current = await localDay(patientId);
      const priorDosesToday = (current.view?.events ?? [])
        .filter((e) => e.eventType === 'DOSE_TAKEN' && e.medId !== undefined)
        .map((e) => ({
          eventId: e.eventId,
          medId: e.medId ?? '',
          slotId: e.slotId ?? null,
          takenAtUtc: e.takenAtUtc,
        }));

      const at = takenAt ?? new Date();
      const warning =
        med && regimen
          ? warningForDose({ med, slotId, priorDosesToday, at, tz: regimen.tz, isCorrection })
          : undefined;

      const write = (extra: boolean) => () =>
        commit(
          {
            eventType: 'DOSE_TAKEN',
            medId,
            medVersion,
            quantity,
            slotId,
            // A time the person chose afterwards is recall, not observation, and the
            // doctor report footnotes it as such.
            timeSource: takenAt ? 'ADJUSTED' : 'NOW',
            overdoseWarningShown: extra,
            warningAcknowledged: extra,
          },
          takenAt,
        );

      if (warning) {
        // Held, not blocked: the person answers and the dose is recorded either way.
        setPendingDose({ warning, log: write(true) });
        return;
      }
      await write(false)();
    },
    [commit, patientId],
  );

  /**
   * Undo a just-logged entry.
   *
   * Removed from the outbox if it never left the device; soft-voided on the server if
   * it did. Either way the row is withdrawn rather than deleted, so a correction stays
   * part of the record.
   */
  const undo = useCallback(
    async (eventId: string) => {
      setUndoing(true);
      try {
        const removedLocally = await unqueue(eventId);
        if (!removedLocally) {
          await apiFetch(config, `/v1/patients/${encodeURIComponent(patientId)}/events/${eventId}`, {
            method: 'DELETE',
          });
        }
        setJustLogged(undefined);
        const local = await render();
        const date = local.view?.logicalDate ?? new Date().toISOString().slice(0, 10);
        await refreshAll(config, patientId, date);
        await render();
      } finally {
        setUndoing(false);
      }
    },
    [config, patientId, render],
  );

  if (loading) return <main className="app"><p className="muted">Loading…</p></main>;

  if (!day) {
    return (
      <main className="app">
        <h1>Med Log</h1>
        <p className="muted">
          We could not load the medication list yet. Once you have opened this with a
          connection, it will keep working without one.
        </p>
      </main>
    );
  }

  /*
   * Undo is offered on ANY dose logged today, not only one this browser session
   * happens to remember.
   *
   * It was previously driven by component state set at the moment of tapping, so a
   * reload — or simply coming back later — left a recorded dose with no way to correct
   * it at all. A mistaken entry in a medication log is the exact thing this product
   * exists to prevent, so the correction cannot depend on not having refreshed.
   *
   * `justLogged` now only drives the "just recorded" emphasis.
   */
  const freshlyLogged =
    justLogged !== undefined && Date.now() - justLogged.at < UNDO_WINDOW_MS ? justLogged : undefined;

  if (timing) {
    return (
      <TimePicker
        title={timing.replacesEventId ? 'Change the time' : 'When did you take it?'}
        medName={timing.medName}
        busy={undoing}
        onCancel={() => {
          setTiming(undefined);
        }}
        onChoose={(at) => {
          const pending = timing;
          setTiming(undefined);
          void (async () => {
            // Correcting a time is a withdrawal and a fresh entry, not a silent edit:
            // the log should show that something was recorded and then corrected.
            const isCorrection = pending.replacesEventId !== undefined;
            if (isCorrection) await undo(pending.replacesEventId ?? '');
            await logDose(
              pending.medId,
              pending.medVersion,
              pending.slotId,
              pending.quantity,
              at,
              isCorrection,
            );
          })();
        }}
      />
    );
  }

  if (flow === 'team') {
    return (
      <CareTeam
        config={config}
        patientId={patientId}
        patientName={day.patientName}
        onBack={() => {
          setFlow('today');
        }}
      />
    );
  }

  if (flow === 'symptom') {
    return (
      <SymptomFlow
        onCancel={() => {
          setFlow('today');
        }}
        onSubmit={async (s: SymptomSubmission) => {
          await commit({ eventType: 'SYMPTOM', timeSource: 'NOW', onset: s.onset, symptoms: s.symptoms, ...(s.severity ? { severity: s.severity } : {}), ...(s.notes ? { notes: s.notes } : {}) });
        }}
      />
    );
  }

  if (flow === 'extra') {
    return (
      <ExtraFlow
        onCancel={() => {
          setFlow('today');
        }}
        onSubmit={async (e: ExtraSubstanceSubmission) => {
          await commit({ eventType: 'EXTRA_SUBSTANCE', timeSource: 'NOW', ...e });
        }}
      />
    );
  }

  if (flow === 'vitals') {
    return (
      <VitalsFlow
        onCancel={() => {
          setFlow('today');
        }}
        onSubmit={async (v: VitalsSubmission) => {
          await commit({ eventType: 'VITALS', timeSource: 'NOW', ...v });
        }}
      />
    );
  }

  return (
    <main className="app">
      {/* Test data announces itself, permanently and before anything else. Mistaking a
          test chart for a real one is precisely the class of error this product exists
          to prevent. */}
      {day.isTest && (
        <div className="test-banner" role="status">
          <strong>TEST PATIENT</strong> — practice data. Nothing here is a real medication
          record.
        </div>
      )}

      {patients.length > 1 && onSelectPatient && (
        <nav className="picker" aria-label="Choose whose log to view">
          {patients.map((p) => (
            <button
              key={p.patientId}
              className={`picker-tab ${p.patientId === patientId ? 'active' : ''}`}
              aria-current={p.patientId === patientId}
              onClick={() => {
                onSelectPatient(p.patientId);
              }}
            >
              {p.displayName}
              {p.isTest ? ' (test)' : ''}
            </button>
          ))}
        </nav>
      )}

      <h1>{readOnly ? day.patientName : 'Today'}</h1>
      <p className="muted">{formatDay(day.logicalDate)}</p>

      {day.banners.map((banner) => (
        <BannerCard key={`${banner.kind}-${banner.medId}`} banner={banner} />
      ))}

      {day.doses.map((dose) => (
        <DoseRow
          key={`${dose.medId}-${dose.slotId}`}
          dose={dose}
          readOnly={readOnly}
          highlighted={freshlyLogged?.eventId === dose.takenEventId}
          undoing={undoing}
          onUndo={
            dose.takenEventId !== undefined
              ? () => {
                  void undo(dose.takenEventId ?? '');
                }
              : undefined
          }
          onPickTime={() => {
            setTiming({
              medId: dose.medId,
              medVersion: dose.medVersion,
              slotId: dose.slotId,
              quantity: dose.quantity,
              medName: dose.medName,
              ...(dose.takenEventId !== undefined
                ? { replacesEventId: dose.takenEventId }
                : {}),
            });
          }}
          onLog={() => {
            void logDose(dose.medId, dose.medVersion, dose.slotId, dose.quantity);
          }}
        />
      ))}

      {day.prn.length > 0 && <h2>As needed</h2>}
      {day.prn.map((prn) => (
        <PrnRow
          key={prn.medId}
          prn={prn}
          readOnly={readOnly}
          undoing={undoing}
          onUndo={
            prn.lastTakenEventId !== undefined
              ? () => {
                  void undo(prn.lastTakenEventId ?? '');
                }
              : undefined
          }
          onLog={() => {
            void logDose(prn.medId, prn.medVersion, null, 1);
          }}
        />
      ))}

      {readOnly && (
        <div className="btn-row">
          {/* The whole point of the log: something to hand a clinician. */}
          <button
            className="btn-quiet big"
            disabled={reportState === 'building'}
            onClick={() => {
              void buildReport(config, patientId, setReportState);
            }}
          >
            {reportState === 'building' ? 'Building the report…' : 'Report for the doctor'}
          </button>
          {reportState === 'error' && (
            <p className="error">Could not build the report. Please try again.</p>
          )}
          <button className="btn-quiet big" onClick={() => { setFlow('team'); }}>
            Who can see this
          </button>
        </div>
      )}

      {!readOnly && (
        <div className="btn-row">
          {/* The three things worth recording that are not a scheduled dose. Always
              present, never behind a menu — navigation is a tax paid while impaired. */}
          <button className="btn-quiet big" onClick={() => { setFlow('symptom'); }}>
            I don’t feel right
          </button>
          <button className="btn-quiet big" onClick={() => { setFlow('extra'); }}>
            I took something else
          </button>
          {vitalsOn ? (
            <button className="btn-quiet big" onClick={() => { setFlow('vitals'); }}>
              Add a reading
            </button>
          ) : (
            <button
              className="btn-quiet subtle"
              onClick={() => {
                setVitalsOn(true);
                void setMeta('vitalsEnabled', true);
              }}
            >
              Also track blood pressure and pulse
            </button>
          )}
        </div>
      )}

      {day.events.length > 0 && (
        <>
          <h2>Logged today</h2>
          {day.events.map((e) => (
            <div className="timeline-row" key={e.eventId}>
              <span className="timeline-time">{to12Hour(e.takenAtLocal)}</span>
              <span>
                {describeEvent(e.eventType, e.summary)}
                {e.redFlag === true && <span className="flag"> · told to call 911</span>}
                {e.actorRole === 'CAREGIVER' && (
                  <span className="by"> · reported by {e.actorName ?? 'a caregiver'}</span>
                )}
              </span>
            </div>
          ))}
        </>
      )}

      <p className="disclaimer">
        Patient-reported log — not a clinical record.
        {onSignOut && (
          <>
            {' · '}
            <button
              className="btn-quiet"
              style={{ minHeight: 'auto', padding: '0.2rem 0.5rem', fontSize: '0.95rem' }}
              onClick={onSignOut}
            >
              Sign out
            </button>
          </>
        )}
      </p>

      <StatusStrip pending={pending} online={online} />

      {pendingDose && (
        <OverdoseSheet
          warning={pendingDose.warning}
          busy={false}
          onConfirm={() => {
            const { log } = pendingDose;
            setPendingDose(undefined);
            void log();
          }}
          onCancel={() => {
            setPendingDose(undefined);
          }}
        />
      )}
    </main>
  );
}

function DoseRow({
  dose,
  readOnly,
  highlighted,
  undoing,
  onLog,
  onUndo,
  onPickTime,
}: {
  dose: DoseCard;
  readOnly: boolean;
  highlighted: boolean;
  undoing?: boolean;
  onLog: () => void;
  onUndo?: (() => void) | undefined;
  onPickTime: () => void;
}) {
  const cls = dose.status === 'TAKEN' ? 'taken' : dose.status === 'DUE' ? 'due' : dose.status === 'MISSED' ? 'missed' : '';
  return (
    <section className={`card ${cls}`} aria-label={`${dose.medName}, ${dose.slotLabel}`}>
      <div className="card-head">
        <span className="med">{dose.medName}</span>
        <span className="when muted">{to12Hour(dose.localTime)}</span>
      </div>
      <div className="slot">
        {dose.slotLabel} · {dose.medLabel}
      </div>

      {dose.status === 'TAKEN' ? (
        <>
          {/* The confirmation has to be unmissable and has to STAY. A brief flash that
              then disappears reads as a failure, not a success. */}
          <div className={`taken-line ${highlighted ? 'fresh' : ''}`} role="status">
            <span className="check" aria-hidden="true">
              ✓
            </span>
            <span>
              <strong>Taken at {to12Hour(dose.takenAtLocal ?? dose.localTime)}</strong>
              {highlighted ? <span className="just-now"> · just recorded</span> : null}
            </span>
          </div>
          {/* No confirmation dialog anywhere in the app — an explicit undo instead,
              because a confirm dialog is one more thing to mis-tap while impaired. */}
          {!readOnly && (
            <div className="after-actions">
              <button className="btn-quiet undo" disabled={undoing === true} onClick={onPickTime}>
                Change the time
              </button>
              {onUndo && (
                <button className="btn-quiet undo" disabled={undoing === true} onClick={onUndo}>
                  {undoing === true ? 'Undoing…' : 'Undo'}
                </button>
              )}
            </div>
          )}
        </>
      ) : (
        !readOnly && (
          <>
            <button className="btn-primary" onClick={onLog}>
              I took it
            </button>
            {/* The common real case: taken twenty minutes ago and only being logged
                now. Recording "now" instead would quietly corrupt the one number the
                whole log exists to capture. */}
            <button className="btn-quiet took-earlier" onClick={onPickTime}>
              I took it earlier…
            </button>
          </>
        )
      )}
      {dose.status === 'MISSED' && !readOnly && (
        <p className="muted" style={{ marginBottom: 0 }}>
          This one is past its usual time. You can still log it.
        </p>
      )}
    </section>
  );
}

function PrnRow({
  prn,
  readOnly,
  undoing,
  onLog,
  onUndo,
}: {
  prn: PrnCard;
  readOnly: boolean;
  undoing?: boolean;
  onLog: () => void;
  onUndo?: (() => void) | undefined;
}) {
  return (
    <section className="card" aria-label={prn.medName}>
      <div className="card-head">
        <span className="med">{prn.medName}</span>
        <span className="when muted">
          {prn.takenTodayCount > 0 ? `${String(prn.takenTodayCount)} today` : 'as needed'}
        </span>
      </div>
      <div className="slot">{prn.medLabel}</div>
      {prn.lastTakenAtLocal !== undefined && (
        <div className="taken-line" role="status">
          <span className="check" aria-hidden="true">
            ✓
          </span>
          <span>
            <strong>Last taken at {to12Hour(prn.lastTakenAtLocal)}</strong>
          </span>
        </div>
      )}
      {!readOnly && (
        <button className="btn-primary" onClick={onLog}>
          I took one
        </button>
      )}
      {!readOnly && onUndo && (
        <button className="btn-quiet undo" disabled={undoing === true} onClick={onUndo}>
          {undoing === true ? 'Undoing…' : 'Undo the last one'}
        </button>
      )}
    </section>
  );
}

function BannerCard({ banner }: { banner: Banner }) {
  const unconfirmed = banner.kind === 'UNCONFIRMED_FREQUENCY';
  return (
    <aside className={`banner ${unconfirmed ? 'unconfirmed' : ''}`}>
      <div className="banner-title">{banner.title}</div>
      <div className="banner-body">{banner.body}</div>
      {/* One tap from noticing the gap to closing it. */}
      {unconfirmed && banner.pharmacyPhone !== undefined && (
        <a className="call-link" href={`tel:${banner.pharmacyPhone}`}>
          Call {banner.pharmacyName ?? 'the pharmacy'}
        </a>
      )}
    </aside>
  );
}

/** Never lets anyone assume a queued dose has been delivered. */
function StatusStrip({ pending, online }: { pending: number; online: boolean }) {
  if (pending === 0 && online) return null;
  return (
    <div className={`strip ${online ? '' : 'offline'}`} role="status">
      {pending > 0
        ? `${String(pending)} ${pending === 1 ? 'entry' : 'entries'} saved on this phone, waiting to sync`
        : 'No connection — your entries are saved here and will sync later'}
    </div>
  );
}

/**
 * Build the PDF and open it.
 *
 * The link is short-lived and opened in a new tab rather than emailed: an emailed PDF
 * of someone's medication history lives forever in a mailbox and in whatever the
 * surgery does with it.
 */
async function buildReport(
  config: AppConfig,
  patientId: string,
  setState: (s: 'idle' | 'building' | 'error') => void,
): Promise<void> {
  setState('building');
  try {
    const month = new Date().toISOString().slice(0, 7);
    const result = await apiFetch<{ url: string }>(
      config,
      `/v1/patients/${encodeURIComponent(patientId)}/reports/monthly?month=${month}`,
      { method: 'POST' },
    );
    window.open(result.url, '_blank', 'noopener');
    setState('idle');
  } catch {
    setState('error');
  }
}

/** Plain-language labels for the timeline; the raw enum means nothing to a reader. */
function describeEvent(eventType: string, summary: string): string {
  switch (eventType) {
    case 'DOSE_TAKEN':
      return summary;
    case 'SYMPTOM':
      return `Felt: ${summary.toLowerCase().replace(/_/g, ' ')}`;
    case 'EXTRA_SUBSTANCE':
      return `Also took: ${summary}`;
    case 'VITALS':
      return 'Recorded a reading';
    default:
      return summary;
  }
}

function to12Hour(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  if (h === undefined || m === undefined) return hhmm;
  const suffix = h < 12 ? 'AM' : 'PM';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${String(hour)}:${String(m).padStart(2, '0')} ${suffix}`;
}

function formatDay(logicalDate: string): string {
  const [y, m, d] = logicalDate.split('-').map(Number);
  if (y === undefined || m === undefined || d === undefined) return logicalDate;
  return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
}
